import { getNestedSubelementsByType, getMostRecentFieldValue, getPersonName } from '@fs/zion-sls'

/**
 * Extracts primary event information from a record
 * @param {Object} record - The record element
 * @returns {Object} Primary event details
 */
export const getRecordPrimaryEventData = (record) => {
  const recordEvents = getNestedSubelementsByType(record, 'EVENT') || []
  const primaryEvent = recordEvents.find((ev) => ev?.primary)

  const date = getNestedSubelementsByType(primaryEvent, 'DATE', true)?.[0]
  const place = getNestedSubelementsByType(primaryEvent, 'PLACE', true)?.[0]

  return {
    event: primaryEvent,
    date: getMostRecentFieldValue(date),
    place: getMostRecentFieldValue(place),
  }
}

/**
 * Formats record display data as a string
 * @param {Object} record - The record element
 * @returns {{primaryEvent: Object, primaryEventSummary: string}} Formatted record display data and event object
 */
export const formatRecordDisplayData = (record) => {
  const typeLabel = record?.displayLabel ?? record?.label
  // When creating new records, the default type is "RECORD" which is not informative to display, so we omit it
  const typeLabelToDisplay = typeLabel !== 'RECORD' ? typeLabel : null
  const { date, place, event } = getRecordPrimaryEventData(record)

  return { primaryEvent: event, primaryEventSummary: [typeLabelToDisplay, date, place].filter(Boolean).join(' | ') }
}

/**
 * Gets a household's persons.
 *
 * @param {Object} household - The household/record SLS object
 * @returns {Array<Object>} The household's person elements
 */
const getHouseholdPersons = (household) => {
  const directPersons = (household?.subElements ?? []).filter((sub) => sub?.elementType === 'PERSON')

  return directPersons.length ? directPersons : getNestedSubelementsByType(household, 'PERSON') || []
}

// Census collections transcribe this per person; the head's own value reads "Head".
const HEAD_OF_HOUSEHOLD_VALUE = 'head'

/**
 * Reads a person's transcribed relationship to the head of their household.
 *
 * @param {Object} person - The person element
 * @returns {string|undefined} The relationship value, or undefined when the field is not indexed
 */
const getRelationshipToHeadValue = (person) => {
  const relationshipToHead = getNestedSubelementsByType(person, 'RELATIONSHIP_TO_HEAD')?.[0]

  return getMostRecentFieldValue(relationshipToHead)
}

/**
 * Finds a household's primary person.
 *
 * Primary is not the same thing as head of household: a record can have several primary persons,
 * and where a household continues from another image SLS flags whoever lands on line 1.
 *
 * @param {Object} household - The household/record SLS object
 * @returns {Object|undefined} The first primary person, or undefined if there is none
 */
export const getPrimaryPerson = (household) => {
  return getHouseholdPersons(household).find((person) => person?.primary)
}

/**
 * Finds a household's head.
 *
 * Census records index the answer in RELATIONSHIP_TO_HEAD, so read that rather than guessing from
 * the primary flag or from the order persons happen to be stored in. Persons on other images count,
 * since a household continuing onto this image has its head on the previous one. Records without
 * the field fall back to the primary person.
 *
 * @param {Object} household - The household/record SLS object
 * @returns {Object|undefined} The head of household, or undefined if there is none
 */
export const getHeadOfHousehold = (household) => {
  const heads = getHouseholdPersons(household).filter(
    (person) => getRelationshipToHeadValue(person)?.trim().toLowerCase() === HEAD_OF_HOUSEHOLD_VALUE
  )

  if (!heads.length) return getPrimaryPerson(household)

  return heads.find((person) => person?.primary) ?? heads[0]
}

/**
 * Gets the name of a household's head, for the group heading.
 *
 * @param {Object} household - The household/record SLS object
 * @returns {string|null} The head of household's name, or null if there is none
 */
export const getHeadOfHouseholdName = (household) => {
  const headOfHousehold = getHeadOfHousehold(household)

  return headOfHousehold ? getPersonName({ person: headOfHousehold, justOneName: true }) : null
}
